/** @jsxImportSource @emotion/react */
import { useState } from "react";
import {
    Modal, ModalBody, ModalHeader
} from "reactstrap";
import { checkImageSizeAndShrinkIfNeeded } from "../../../image-utils";
import { ImageCarousel } from "./ImageCarousel";
import { ImageThumbnailGrid } from "./ImageThumbnailGrid";
import { ImageUploader } from "./ImageUploader";

interface FilesModalProps {
    requiredImage: any;
    images: any[];
    onClose: () => void;
    onSaveImage: (imageType: string, base64: string, focusMode: any) => Promise<any>;
    onDeleteImage: (image: any) => void;
    onOpenCamera: () => void;
    isLoading: boolean;
}

export function FilesModal({
    requiredImage,
    images,
    onClose,
    onSaveImage,
    onDeleteImage,
    onOpenCamera,
    isLoading
}: FilesModalProps) {
    const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);

    const onUploadImage = async (base64: string, focusMode: any) => {
        if (base64) {
            const img = new Image();
            img.src = base64;
            await new Promise((resolve) => { img.onload = resolve; });
            const resizedBase64 = await checkImageSizeAndShrinkIfNeeded(base64, img.width, img.height);
            if (resizedBase64) {
                await onSaveImage(requiredImage.type, resizedBase64, focusMode);
            }
        }
    };

    // Filter images to show only those matching the required image type
    const filteredImages = images.filter((img) => img.type === requiredImage.type);

    // Map images for carousel
    const carouselImages = filteredImages.map((image) => ({
        data_url: image.url_webp || image.url,
        id: image.image_id || image.id,
        original: image
    }));

    return (
        <Modal isOpen={true} className="modal-xl" fullscreen={true}>
            <ModalHeader toggle={onClose}>{requiredImage.label}</ModalHeader>
            <ModalBody>
                <ImageUploader
                    onSaveImage={onUploadImage}
                    onOpenCamera={onOpenCamera}
                    images={filteredImages}
                    isLoading={isLoading}
                />

                <ImageThumbnailGrid
                    images={carouselImages}
                    activeIndex={activeCarouselIndex}
                    onImageClick={(index) => setActiveCarouselIndex(index)}
                    onDeleteImage={onDeleteImage}
                />

                {carouselImages.length > 0 && (
                    <ImageCarousel
                        images={carouselImages}
                        activeIndex={activeCarouselIndex}
                        onIndexChange={setActiveCarouselIndex}
                        onDeleteImage={onDeleteImage}
                    />
                )}
            </ModalBody>
        </Modal>
    );
}
